/* eslint-disable @reatom/unit-naming-rule */
import { action, atom } from '@reatom/framework'
import BigNumber from 'bignumber.js'

import { Result } from '@blade/std-lib'
import { reatomCurrencyInput } from '@root/features/currency/currency-input-reatom'
import { processBuySwapAction } from '@root/features/trade/swap'
import { tradeSettingsAtom } from '@root/features/trade/trade-settings'
import {
  tradingPairAtom,
  tradingPairBaseAtom,
  tradingWalletAtom,
} from '@root/features/trade/trading/atoms'
import {
  InsufficientBalanceError,
  NoAmountError,
} from '@root/features/trade/validation-errors'
import { Error } from '@root/shared'
import { reatomRequest } from '@root/shared/lib'

import { reatomControlsQuote } from '../reatom-controls-quote'

export const reatomMarketBuyControls = () => {
  const amountInputModel = reatomCurrencyInput(
    { currencyAtom: tradingPairBaseAtom, walletAtom: tradingWalletAtom },
    'amountInputModel',
  )

  const quoteModel = reatomControlsQuote({
    amountAtom: amountInputModel.amountAtom,
    direction: 'buy',
    name: 'quoteModel',
  })

  const validationAtom = atom(
    (ctx): InsufficientBalanceError | NoAmountError | undefined => {
      const amount = ctx.spy(amountInputModel.amountAtom)
      const balance = ctx.spy(amountInputModel.balanceAtom)
      const currencyIn = ctx.get(tradingPairBaseAtom)

      if (amount.gt(balance)) {
        return new InsufficientBalanceError({
          amount,
          balance,
          currency: currencyIn,
        })
      }
      if (amount.isZero()) {
        return new NoAmountError()
      }
      return undefined
    },
    'validationAtom',
  )

  const executeSwapInternalAction = action(async (ctx) => {
    const pair = ctx.get(tradingPairAtom)
    const wallet = ctx.get(tradingWalletAtom)

    const settings = ctx.get(tradeSettingsAtom)
    const amountIn = ctx.get(amountInputModel.amountAtom)

    if (amountIn.eq(0) || !wallet || !pair) {
      return Result.fail(Error.UnknownError.fromUnknown('invalid input'))
    }

    const quote = ctx.get(quoteModel.quoteStateAtom).value

    return processBuySwapAction(ctx, {
      amount: amountIn,
      pair: pair,
      wallet: wallet,
      settings: settings,
      quote,
    })
  }, 'executeSwapInternalAction')

  const executeSwapModel = reatomRequest({
    execAction: executeSwapInternalAction,
    name: `executeSwapModel`,
    concurrently: false,
  })

  const receiveAmountCurrencyAtom = atom((ctx) => {
    const pair = ctx.spy(tradingPairAtom)
    if (!pair) {
      return BigNumber(0)
    }
    const priceNative = ctx.spy(pair.priceRealtime).priceNative
    if (priceNative.isZero()) {
      return BigNumber(0)
    }

    const amountNative = ctx.spy(amountInputModel.amountAtom)
    const amountCurrency = amountNative.dividedBy(priceNative)
    // TODO: Calculate considering fees
    return amountCurrency
  }, 'receiveAmountCurrencyAtom')

  return {
    amountInputModel,
    validationAtom,
    executeSwapModel,
    quoteModel,
    receiveAmountCurrencyAtom,
    walletAtom: tradingWalletAtom,
  }
}
export type MarketBuyControlsModel = ReturnType<typeof reatomMarketBuyControls>
